// Localization for the Audit Log's system-generated text.
//
// Audit rows are written to the database as finished English sentences, and
// they are a legal record — the stored value is never rewritten. Only the
// DISPLAY is localized: the sentence is pattern-matched back into a key plus
// its captured values, then re-rendered in the active language. Captured
// values (emails, IPs, names, codes) are data and pass through untranslated.
//
// Anything that does not match a known shape is shown exactly as stored, so a
// new or unrecognized event is never hidden or mangled.
import { localizeSystemText } from './timelineI18n';

const P = (re, key, map) => ({ re, key, map });

// Ordered most specific first. Keys live under audit.descriptions.*
const PATTERNS = [
  P(/^User (.+) logged in$/, 'userLoggedIn', (m) => ({ email: m[1] })),
  P(/^Platform admin "(.+)" logged in$/, 'platformAdminLoggedIn', (m) => ({ email: m[1] })),
  P(/^(.+) started impersonating (.+)$/, 'impersonationStarted', (m) => ({ actor: m[1], target: m[2] })),
  P(/^Impersonation of (.+) ended$/, 'impersonationEnded', (m) => ({ target: m[1] })),
  P(/^User (.+) role changed from (.+) to (.+)$/, 'roleChanged', (m) => ({ email: m[1], from: m[2], to: m[3] })),
  P(/^User (.+) was permanently deleted$/, 'userDeleted', (m) => ({ email: m[1] })),

  P(/^Tenant default language set to "(.+)"$/, 'tenantLanguageSet', (m) => ({ lang: m[1] })),
  P(/^Organization settings updated$/, 'organizationUpdated', () => ({})),
  P(/^Branding settings updated$/, 'brandingUpdated', () => ({})),
  P(/^Branding (\w+) image uploaded$/, 'brandingImageUploaded', (m) => ({ kind: m[1] })),
  P(/^Custom role permissions updated$/, 'rolePermissionsUpdated', () => ({})),

  P(/^Contract "(.+)" was created$/, 'contractCreated', (m) => ({ name: m[1] })),
  P(/^Contract "(.+)" created$/, 'contractCreated', (m) => ({ name: m[1] })),
  P(/^Contract "(.+)" updated$/, 'contractUpdated', (m) => ({ name: m[1] })),
  P(/^Contract "(.+)" reopened for editing \(Request Edit\) — (\d+) document\(s\) unlocked$/,
    'contractReopened', (m) => ({ name: m[1], count: m[2] })),
  P(/^Amendment V(\d+) \("(.+)"\) created from contract "(.+)": ([\s\S]*)$/,
    'amendmentCreated', (m) => ({ num: m[1], number: m[2], contract: m[3], note: m[4] })),
  P(/^Addendum V(\d+) \("(.+)"\) created from contract "(.+)": ([\s\S]*)$/,
    'addendumCreated', (m) => ({ num: m[1], number: m[2], contract: m[3], note: m[4] })),
  P(/^Version V(\d+) \((\w+)\) of contract "(.+)" became the active version$/,
    'versionActivated', (m) => ({ num: m[1], type: m[2], contract: m[3] })),
  P(/^Responsible person of contract "(.+)" changed from (.+) to (.+): ([\s\S]*)$/,
    'responsibleUpdated', (m) => ({ contract: m[1], from: m[2], to: m[3], note: m[4] })),

  P(/^(\d+) task\(s\) reassigned from (.+?) to (.+?): ([\s\S]*)$/,
    'tasksReassigned', (m) => ({ count: m[1], from: m[2], to: m[3], reason: m[4] })),
  P(/^Task reassigned from (.+?) to (.+?): ([\s\S]*)$/,
    'taskReassigned', (m) => ({ from: m[1], to: m[2], reason: m[3] })),

  P(/^Workflow assignee replaced: (\d+) usage\(s\) of (.+?) replaced with (.+?) across (\d+) workflow\(s\)$/,
    'workflowAssigneeReplaced', (m) => ({ count: m[1], from: m[2], to: m[3], workflows: m[4] })),
  P(/^Workflow "(.+)" assignee replaced: (.+?) → (.+)$/,
    'workflowAssigneeReplacedOne', (m) => ({ name: m[1], from: m[2], to: m[3] })),

  P(/^Workflow "(.+)" started for contract "(.+)"$/, 'workflowStarted', (m) => ({ name: m[1], contract: m[2] })),
  P(/^Workflow "(.+)" deleted$/, 'workflowDeleted', (m) => ({ name: m[1] })),
  P(/^Step "(.+)" approved$/, 'stepApproved', (m) => ({ step: m[1] })),
  P(/^Step "(.+)" rejected: ([\s\S]*)$/, 'stepRejected', (m) => ({ step: m[1], comments: m[2] })),

  P(/^Role "(.+)" created$/, 'roleCreated', (m) => ({ name: m[1] })),
  P(/^Role "(.+)" updated$/, 'roleUpdated', (m) => ({ name: m[1] })),
  P(/^Role "(.+)" deleted$/, 'roleDeleted', (m) => ({ name: m[1] })),
  P(/^Department "(.+)" created$/, 'departmentCreated', (m) => ({ name: m[1] })),
  P(/^Department "(.+)" updated$/, 'departmentUpdated', (m) => ({ name: m[1] })),
  P(/^Department "(.+)" deleted$/, 'departmentDeleted', (m) => ({ name: m[1] })),
  P(/^Department "(.+)" added$/, 'departmentCreated', (m) => ({ name: m[1] })),

  P(/^Template "(.+)" created$/, 'templateCreated', (m) => ({ name: m[1] })),
  P(/^Template "(.+)" updated to v(\d+)$/, 'templateUpdated', (m) => ({ name: m[1], version: m[2] })),
  P(/^Contract template "(.+)" created$/, 'contractTemplateCreated', (m) => ({ name: m[1] })),
  P(/^Contract template "(.+)" updated \((\d+) mapped fields\)$/,
    'contractTemplateUpdated', (m) => ({ name: m[1], count: m[2] })),

  P(/^Obligation "(.+)" created$/, 'obligationCreated', (m) => ({ name: m[1] })),
  P(/^Obligation "(.+)" updated$/, 'obligationUpdated', (m) => ({ name: m[1] })),

  P(/^Signature integration "(.+)" connected \((.+)\)$/, 'integrationConnected', (m) => ({ name: m[1], env: m[2] })),
  P(/^Signature integration "(.+)" updated$/, 'integrationUpdated', (m) => ({ name: m[1] })),
  P(/^Signature integration "(.+)" disconnected$/, 'integrationDisconnected', (m) => ({ name: m[1] })),

  P(/^Version (\d+) uploaded for document "(.+)"$/, 'documentVersionUploaded', (m) => ({ version: m[1], doc: m[2] })),
  P(/^Document version (\d+) saved by (.+) via editor$/, 'documentVersionSaved', (m) => ({ version: m[1], name: m[2] })),
  P(/^File "(.+)" uploaded to library folder "(.+)"$/, 'libraryFileUploaded', (m) => ({ file: m[1], folder: m[2] })),
  P(/^Folder "(.+)" created in library of contract "(.+)"$/, 'libraryFolderCreatedIn', (m) => ({ folder: m[1], contract: m[2] })),
  P(/^Library folder "(.+)" created$/, 'libraryFolderCreated', (m) => ({ folder: m[1] })),
  P(/^Approved document downloaded from the QR verification page$/, 'qrDocumentDownloaded', () => ({})),

  P(/^Invitation sent to (.+)$/, 'externalInvitationSent', (m) => ({ email: m[1] })),
  P(/^(.+) opened the review link$/, 'externalLinkOpened', (m) => ({ name: m[1] })),

  P(/^Tenant "(.+)" suspended$/, 'tenantSuspended', (m) => ({ name: m[1] })),
  P(/^Tenant "(.+)" provisioned: (\d+) users, (\d+) departments, (\d+) permission profiles$/,
    'tenantProvisioned', (m) => ({ name: m[1], users: m[2], departments: m[3], profiles: m[4] })),
];

/** Event type label ("Login" → "Giriş Yapıldı"). */
export function auditEventLabel(type, t) {
  if (!type) return '';
  return t(`audit.eventTypes.${type}`, { defaultValue: type.replace(/_/g, ' ') });
}

/** Entity/category label ("settings" → "Ayarlar"). */
export function auditEntityLabel(type, t) {
  if (!type) return '';
  return t(`audit.entityTypes.${type}`, { defaultValue: type.replace(/_/g, ' ') });
}

/**
 * Localize a stored audit description. Falls back to the timeline's matcher —
 * it already covers the audit sentences the contract timeline replays — and
 * finally to the stored text itself.
 */
export function auditDescription(text, t, lang) {
  if (!text || typeof text !== 'string') return text;
  for (const { re, key, map } of PATTERNS) {
    const m = text.match(re);
    if (m) return t(`audit.descriptions.${key}`, { ...map(m), defaultValue: text });
  }
  return localizeSystemText(text, t, lang);
}
